import { useState } from "react";
import { Loader2, Download, LogOut } from "lucide-react";
import Seo from "../components/Seo";
import { adminLogin, fetchLeads, exportLeadsUrl } from "../lib/api";

const inputCls =
  "w-full rounded-sm border border-input bg-white px-3 py-2.5 text-sm text-brand-navy transition-colors duration-150 focus:border-primary";

export default function Admin() {
  const [password, setPassword] = useState("");
  const [token, setToken] = useState(() => sessionStorage.getItem("gpsl-admin-token") || "");
  const [leads, setLeads] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async (t) => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchLeads(t);
      setLeads(data.leads);
    } catch {
      setError("Session expired or invalid. Please sign in again.");
      setToken("");
      sessionStorage.removeItem("gpsl-admin-token");
    } finally {
      setLoading(false);
    }
  };

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const t = await adminLogin(password);
      setToken(t);
      sessionStorage.setItem("gpsl-admin-token", t);
      await load(t);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Login failed.");
    } finally {
      setLoading(false);
    }
  };

  const downloadCsv = async () => {
    const res = await fetch(exportLeadsUrl(), { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "leads.csv";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  if (token && leads === null && !loading && !error) load(token);

  return (
    <>
      <Seo path="/admin" title="Lead Admin" />
      <section className="mx-auto max-w-6xl px-4 py-16 sm:px-8" data-testid="admin-page">
        {!token ? (
          <div className="mx-auto max-w-sm">
            <h1 className="text-2xl font-semibold tracking-tight text-brand-navy">Lead admin</h1>
            <p className="mt-2 text-sm text-brand-slate">Restricted to GoAndTrack staff.</p>
            <form onSubmit={login} className="mt-6 space-y-4">
              <div>
                <label htmlFor="admin-password" className="mb-1.5 block text-sm font-medium text-brand-navy">Password</label>
                <input id="admin-password" type="password" className={inputCls} value={password}
                  onChange={(e) => setPassword(e.target.value)} data-testid="admin-password-input" autoComplete="current-password" />
              </div>
              {error && <p className="rounded-sm border border-red-300 bg-red-50 p-3 text-sm text-red-800" role="alert" data-testid="admin-error">{error}</p>}
              <button type="submit" disabled={loading || !password} data-testid="admin-login-submit"
                className="inline-flex items-center gap-2 rounded-sm bg-primary px-6 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-40">
                {loading && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />} Sign in
              </button>
            </form>
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <h1 className="text-2xl font-semibold tracking-tight text-brand-navy">Leads</h1>
                <p className="mt-1 text-sm text-brand-slate" data-testid="admin-lead-count">{leads ? `${leads.length} enquiries` : "Loading…"}</p>
              </div>
              <div className="flex gap-3">
                <button onClick={downloadCsv} data-testid="admin-export-csv"
                  className="inline-flex items-center gap-2 rounded-sm border border-border px-4 py-2 text-sm font-semibold text-brand-navy hover:border-primary hover:text-primary">
                  <Download className="h-4 w-4" aria-hidden="true" /> Export CSV
                </button>
                <button onClick={() => { setToken(""); setLeads(null); sessionStorage.removeItem("gpsl-admin-token"); }} data-testid="admin-logout"
                  className="inline-flex items-center gap-2 rounded-sm px-4 py-2 text-sm font-medium text-brand-slate hover:text-primary">
                  <LogOut className="h-4 w-4" aria-hidden="true" /> Sign out
                </button>
              </div>
            </div>
            {error && <p className="mt-4 rounded-sm border border-red-300 bg-red-50 p-3 text-sm text-red-800" role="alert" data-testid="admin-error">{error}</p>}
            <div className="mt-6 overflow-x-auto rounded-sm border border-border">
              <table className="w-full min-w-[900px] border-collapse text-sm" data-testid="admin-leads-table">
                <thead>
                  <tr className="bg-brand-surface text-left font-mono text-xs uppercase tracking-wider text-brand-slate">
                    {["Date", "Name", "Email", "Company", "Interest", "Volume", "Page", "UTM"].map((h) => (
                      <th key={h} scope="col" className="border-b border-border p-3">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(leads || []).map((l, i) => (
                    <tr key={i} className="border-b border-border align-top hover:bg-brand-surface/50">
                      <td className="whitespace-nowrap p-3 font-mono text-xs text-brand-slate">{(l.created_at || "").slice(0, 16).replace("T", " ")}</td>
                      <td className="p-3 font-medium text-brand-navy">{l.name}</td>
                      <td className="p-3 text-brand-slate">{l.email}</td>
                      <td className="p-3 text-brand-slate">{l.company}</td>
                      <td className="p-3 text-brand-slate">{l.interest}</td>
                      <td className="p-3 text-brand-slate">{l.monthly_volume || "-"}</td>
                      <td className="p-3 font-mono text-xs text-brand-slate">{l.page || "-"}</td>
                      <td className="p-3 font-mono text-xs text-brand-slate">{l.utm?.utm_source || "-"}</td>
                    </tr>
                  ))}
                  {leads && leads.length === 0 && (
                    <tr><td colSpan={8} className="p-8 text-center text-brand-slate" data-testid="admin-empty">No enquiries yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>
    </>
  );
}
